import React, { useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { generateAIToolsMaterialPresignedUrl } from '../../api/s3';
import { getDriveFiles } from '../../api/ctrag';
import { documentTranslation } from '../../api/aiTools';
import './translationForm.scss';
import SingleUpload from '../../components/SingleUpload';
import LanguageSelector from '../../components/LanguageSelector';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  openDrivePicker,
  MIME_TYPE_GROUPS,
  DEFAULT_VIEW_ID
} from '../../utils/googlePickerConfig';
import { getDocumentAcceptTypes } from '../../constants/fileTypes';

const { TextArea } = Input;

const TranslationForm = ({ visible, onCancel, onAfterSubmit, user }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [selectedLanguage, setSelectedLanguage] = useState('zh-TW');
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
    
    const predefinedMappingList = [
        { key: '微電能源', value: 'Micro Electricity' },
        // { key: '微電能源', value: 'RFDME' },
        // { key: 'EMS', value: 'EMS' },
        // { key: 'PV O&M', value: 'PV O&M' }
    ];

    // 初始化時就包含預設的對照表
    const [mappingList, setMappingList] = useState([...predefinedMappingList]);

    // 不再需要預先載入舊版 Google Picker API

    const createPicker = async () => {
        try {
            const result = await openDrivePicker({
                mimeTypes: MIME_TYPE_GROUPS.OFFICE_DOCUMENTS,
                includeFolders: true,
                enableDrives: true,
                viewId: DEFAULT_VIEW_ID,
                title: t('aiAssistant.documentTranslation.form.selectDocument'),
                locale: (typeof t === 'function' && (t.i18n?.language)) ? t.i18n.language : undefined,
            });
            if (result?.docs?.length) {
                const file = result.docs[0];
                await handleDriveFile(file);
            } else {
                setIsAttachmentUploading(false);
            }
        } catch (error) {
            console.error(t('aiAssistant.documentTranslation.form.messages.googlePickerCreateFailed'), error);
            
            message.error(t('aiAssistant.documentTranslation.form.messages.googlePickerCreateFailed'));
            setIsAttachmentUploading(false);
        }
    };

    // 舊版 picker callback 已移除，直接透過 openDrivePicker 回傳 docs 處理

    const handleDriveFile = async (file) => {
        const newFile = {
            id: file.id,
            source_name: file.name,
            source_type: file.mimeType,
            source_source: 'drive',
            source_url: file.url,
            source_id: file.id,
            dispatchStatus: 'pending',
            dispatchMessage: '',
            status: 1,
        };

        try {
            setIsAttachmentUploading(true);
            const fileDetails = await getDriveFiles([file.id]);
            if (fileDetails && fileDetails.length > 0) {
                const detailedFile = fileDetails[0].file_info;
                const updatedFile = {
                    ...newFile,
                    source_name: detailedFile.name,
                    source_type: detailedFile.mimeType,
                    source_url: detailedFile.webViewLink,
                    size: detailedFile.size,
                    createdTime: detailedFile.createdTime,
                    status: detailedFile.status || 1,
                };

                if (attachmentFile) {
                    message.warning(t('aiAssistant.documentTranslation.form.messages.singleFileOnly'));
                }
                setAttachmentFile(updatedFile);
                form.setFieldValue('material_path', updatedFile);
            }
        } catch (error) {
            console.error(t('aiAssistant.documentTranslation.form.messages.driveFileProcessFailed'), error);
            message.error(t('aiAssistant.documentTranslation.form.messages.driveFileProcessFailedWithMessage', { message: error.message }));
        } finally {
            setIsAttachmentUploading(false);
        }
    };

    const handleAttachmentFileChange = async (info) => {
        const file = info.file;
        if (attachmentFile) {
            message.warning(t('aiAssistant.documentTranslation.form.messages.singleFileOnly'));
        }

        setIsAttachmentUploading(true);

        try {
            const presignedUrlInfo = await generateAIToolsMaterialPresignedUrl(file.name);
            const formData = new FormData();
            Object.entries(presignedUrlInfo.presigned_post_data.fields).forEach(([key, value]) => {
                formData.append(key, value);
            });
            formData.append('file', file);
            
            await axios.post(presignedUrlInfo.presigned_post_data.url, formData);
            
            const newFile = { 
                source_name: file.name,
                source_type: file.type,
                source_source: 'local',
                source_url: presignedUrlInfo.source_info.s3_url
            };
            
            setAttachmentFile(newFile);
            form.setFieldValue('material_path', newFile);
            message.success(t('aiAssistant.documentTranslation.form.messages.fileUploadSuccess'));
        } catch (error) {
            console.error(t('aiAssistant.documentTranslation.form.messages.fileUploadFailed'), error);
            message.error(t('aiAssistant.documentTranslation.form.messages.fileUploadFailed'));
        } finally {
            setIsAttachmentUploading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!attachmentFile || !values.material_path) {
                message.error(t('aiAssistant.documentTranslation.form.messages.needFile'));
                return;
            }

            if (isAttachmentUploading) {
                message.warning(t('aiAssistant.documentTranslation.form.messages.waitForUpload'));
                return;
            }

            const formattedValues = {
                material_path: attachmentFile.source_url,
                additional_context: values.additional_context,
                target_language: values.target_language,
                constants_mapping: mappingList.reduce((obj, item) => {
                    if (item.key && item.value) {
                        obj[item.key] = item.value;
                    }
                    return obj;
                }, {}),
            };
            const model = {
                model_provider: 'openai',
                model_level: 'basic'
            }

            const result = await documentTranslation({ data: formattedValues, model: model });

            if (result.status === 200) {
                message.success(t('aiAssistant.documentTranslation.form.messages.submitSuccess'));
                onAfterSubmit(result);
                form.resetFields();
                setAttachmentFile(null);
                setMappingList([...predefinedMappingList]);
            } else {
                message.error(result.message || t('aiAssistant.documentTranslation.form.messages.submitFailed'));
            }
        } catch (error) {
            if (error.errorFields) {
                message.error(t('aiAssistant.documentTranslation.form.messages.formIncomplete'));
            } else {
                console.error(t('aiAssistant.documentTranslation.form.messages.submitErrorWithMessage'), error);
                message.error(t('aiAssistant.documentTranslation.form.messages.processingError') + ': ' + (error.message || t('aiAssistant.documentTranslation.form.messages.unknownError')));
            }
        }
    };

    const handleAddMapping = () => {
        setMappingList([...mappingList, { key: '', value: '' }]);
    };

    const handleRemoveMapping = (index) => {
        const newList = mappingList.filter((_, i) => i !== index);
        setMappingList(newList);
    };

    const handleMappingChange = (index, field, value) => {
        const newList = [...mappingList];
        newList[index][field] = value;
        setMappingList(newList);
    };

    return (
        <Modal
            className="translation-modal"
            title={t('aiAssistant.documentTranslation.form.title')}
            open={visible}
            onCancel={onCancel}
            maskClosable={false}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t('aiAssistant.documentTranslation.form.buttons.cancel')}
                </Button>,
                <Button disabled={isAttachmentUploading} key="submit" type="primary" onClick={handleSubmit}>
                    {t('aiAssistant.documentTranslation.form.buttons.submit')}
                </Button>
            ]}
            centered
            width={800}
        >
            <Form
                form={form}
                layout="vertical"
            >
                {/* 要翻譯的文件 */}
                <Form.Item
                    label={t('aiAssistant.documentTranslation.form.fields.documentFile.label')}
                    name="material_path"
                    rules={[{ required: true, message: t('aiAssistant.documentTranslation.form.fields.documentFile.required') }]}
                    valuePropName="file"
                >
                    <SingleUpload
                        file={attachmentFile}
                        isUploading={isAttachmentUploading}
                        onFileChange={handleAttachmentFileChange}
                        onGoogleDriveClick={() => createPicker('attachment')}
                        onDeleteFile={() => {
                            setAttachmentFile(null);
                            form.setFieldValue('material_path', null);
                        }}
                        acceptTypes={getDocumentAcceptTypes()}
                        disabled={isAttachmentUploading}
                    />
                </Form.Item>

                {/* 補充文件資訊 */}
                <Form.Item
                    label={t('aiAssistant.documentTranslation.form.fields.additionalContext.label')}
                    name="additional_context"
                >
                    <TextArea
                        placeholder={t('aiAssistant.documentTranslation.form.fields.additionalContext.placeholder')}
                        rows={4}
                    />
                </Form.Item>

                {/* 目標語言 */}
                <Form.Item
                    label={t('aiAssistant.documentTranslation.form.fields.targetLanguage.label')}
                    name="target_language"
                    initialValue="zh-TW"
                >
                    <LanguageSelector
                        value={selectedLanguage}
                        onChange={value => setSelectedLanguage(value)}
                    />
                </Form.Item>

                {/* 不該被翻譯的關鍵字/專有名詞對照表 */}
                <Form.Item
                    label={t('aiAssistant.documentTranslation.form.fields.constantsMapping.label')}
                    name="constants_mapping"
                >
                    <div style={{ marginBottom: '8px' }}>
                        {mappingList.map((mapping, index) => (
                            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <Input
                                    placeholder={t('aiAssistant.documentTranslation.form.fields.constantsMapping.originalKeyword')}
                                    value={mapping.key}
                                    onChange={(e) => handleMappingChange(index, 'key', e.target.value)}
                                    style={{ width: '45%' }}
                                />
                                <Input
                                    placeholder={t('aiAssistant.documentTranslation.form.fields.constantsMapping.translatedKeyword')}
                                    value={mapping.value}
                                    onChange={(e) => handleMappingChange(index, 'value', e.target.value)}
                                    style={{ width: '45%' }}
                                />
                                <Button
                                    type="text"
                                    danger
                                    onClick={() => handleRemoveMapping(index)}
                                    icon={<DeleteOutlined />}
                                />
                            </div>
                        ))}
                    </div>
                    <Button
                        type="dashed"
                        onClick={handleAddMapping}
                        block
                        icon={<PlusOutlined />}
                    >
                        {t('aiAssistant.documentTranslation.form.fields.constantsMapping.addMapping')}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TranslationForm; 